import z from 'zod';

import { createSliceObject, SliceObject } from './slice-obj-class';
import { SalientPhaseSchema, State } from './state-class';

type SeriesUID = string;

interface SliceInstance {
  seriesUID: string;
  seriesIndex: number;
  sliceIndex: number;
  sliceObject: SliceObject;
}

interface SeriesObject {
  seriesUID: string;
  seriesIndex: number;
  slicesArray: SliceInstance[];
}

export const SerializedStudyDataSchema = z.object({
  seriesObject: z.record(z.string(), z.object({
    seriesUID: z.string(),
    seriesIndex: z.number(),
    slicesArray: z.array(z.object({
      seriesUID: z.string(),
      seriesIndex: z.number(),
      sliceIndex: z.number(),
      sliceObject: z.object({
        excludedImages: z.array(z.string()),
        selectedImages: z.array(z.string()),
        states: z.record(z.string(), z.object({
          color: z.string(),
          start: z.number(),
          end: z.number(),
          numImages: z.number(),
          cardiacCycles: z.object({
            systoles: z.array(z.never()),
            diastoles: z.array(z.never()),
          }),
          duration: z.string(),
          heartrate: z.string(),
          salientPhases: z.array(SalientPhaseSchema),
        })),
      }),
    })),
  })),
});
export type SerializedStudyData = z.infer<typeof SerializedStudyDataSchema>;

export class ActiveStudyData {
  private seriesObject: Record<SeriesUID, SeriesObject>;

  constructor() {
    this.seriesObject = {};
  }

  addDefaultSeries(
    seriesUID: SeriesUID,
    seriesIndex: number,
    sliceIndex: number,
    numPhases: number,
  ) {
    const stateConfig = {
      BREATHHOLD: {
        numImages: numPhases,
      },
    };
    const sliceObject = createSliceObject(stateConfig);

    const sliceInstance = {
      seriesUID,
      seriesIndex,
      sliceIndex,
      sliceObject,
    };

    if (!this.seriesObject[seriesUID]) {
      this.seriesObject[seriesUID] = {
        seriesUID,
        seriesIndex,
        slicesArray: [sliceInstance],
      };
      return;
    }

    this.seriesObject[seriesUID].slicesArray.push(sliceInstance);
  }

  getSliceObjectByIndx(seriesUID: SeriesUID, sliceIndex: number): SliceObject {
    // Check if the series exists
    if (!this.seriesObject[seriesUID]) {
      throw new Error(`Series with UID ${seriesUID} not found`);
    }

    const series = this.seriesObject[seriesUID];

    // Check if the sliceIndex is valid
    if (sliceIndex < 0 || sliceIndex >= series.slicesArray.length) {
      throw new Error(
        `Invalid slice index ${sliceIndex} for series ${seriesUID}. Valid range: 0 to ${
          series.slicesArray.length - 1
        }`,
      );
    }

    return series.slicesArray[sliceIndex].sliceObject;
  }

  getSeriesObject() {
    return this.seriesObject;
  }

  serialize() {
    type SeriesObject = SerializedStudyData['seriesObject'];
    type SlicesArray = SeriesObject[string]['slicesArray'];
    type SliceObject = SlicesArray[number]['sliceObject'];
    type States = SliceObject['states'];

    const serializableData: SerializedStudyData = {
      seriesObject: Object.keys(this.seriesObject).reduce<SeriesObject>(
        (acc, seriesUID) => {
          const series = this.seriesObject[seriesUID];
          acc[seriesUID] = {
            seriesUID: series.seriesUID,
            seriesIndex: series.seriesIndex,
            slicesArray: series.slicesArray.map<SlicesArray[number]>(slice => ({
              seriesUID: slice.seriesUID,
              seriesIndex: slice.seriesIndex,
              sliceIndex: slice.sliceIndex,
              sliceObject: {
                excludedImages: Array.from(
                  slice.sliceObject.getExcludedImages(),
                ),
                selectedImages: Array.from(
                  slice.sliceObject.getSelectedImages(),
                ),
                states: Object.keys(slice.sliceObject.getStates()).reduce<States>(
                  (stateAcc, stateName) => {
                    const state = slice.sliceObject.getState(stateName);
                    return {
                      ...stateAcc,
                      [stateName]: {
                        color: state.getColor(),
                        start: state.getStart(),
                        end: state.getEnd(),
                        numImages: state.getNumImages(),
                        cardiacCycles: state.getCardiacCycles(),
                        duration: state.getDuration(),
                        heartrate: state.getHeartrate(),
                        salientPhases: state.getSalientPhases(),
                      } satisfies States[string],
                    };
                  },
                  {},
                ),
              } satisfies SliceObject,
            } satisfies SlicesArray[number])),
          } satisfies SeriesObject[string];
          return acc;
        },
        {},
      ),
    };
    return serializableData;
  }

  deserialize(data: SerializedStudyData): void {
    this.seriesObject = {};

    Object.keys(data.seriesObject).forEach((seriesUID) => {
      const seriesData = data.seriesObject[seriesUID];
      const slicesArray: SliceInstance[] = seriesData.slicesArray.map(
        (sliceData) => {
          // Reconstruct State objects
          const states: Record<string, State> = {};
          Object.keys(sliceData.sliceObject.states).forEach((stateName) => {
            states[stateName] = State.fromSerialized(
              stateName,
              sliceData.sliceObject.states[stateName],
            );
          });

          // Reconstruct SliceObject
          const sliceObject = new SliceObject(states);
          sliceData.sliceObject.excludedImages.forEach((imageUID: string) => {
            sliceObject.addExcludedImage(imageUID);
          });
          sliceData.sliceObject.selectedImages.forEach((imageUID: string) => {
            sliceObject.addSelectedImage(imageUID);
          });

          return {
            seriesUID: sliceData.seriesUID,
            seriesIndex: sliceData.seriesIndex,
            sliceIndex: sliceData.sliceIndex,
            sliceObject,
          };
        },
      );

      this.seriesObject[seriesUID] = {
        seriesUID: seriesData.seriesUID,
        seriesIndex: seriesData.seriesIndex,
        slicesArray,
      };
    });
  }
}
